import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Trash2, Upload, Send, Image as ImageIcon, Film, X, ShieldCheck } from "lucide-react";

const CATEGORIES = ["school", "tv_channel", "academy", "store", "quiz_olympiad", "myrezz", "xplore", "global"];
const STATUSES = ["draft", "scheduled", "published", "archived"];
const AUDIENCES = ["students", "educators", "parents", "admins", "public"];

const MB = 1024 * 1024;
const fmtBytes = (b) => (b >= 1024 * MB ? (b / (1024 * MB)).toFixed(2) + " GB" : (b / MB).toFixed(1) + " MB");

function StatusPill({ s }) {
  const map = { published: "bg-[#86EFAC]", scheduled: "bg-[#93C5FD]", draft: "bg-[#FDE047]", archived: "bg-slate-300" };
  return <span className={`nb-badge text-[10px] ${map[s] || "bg-slate-200"}`}>{s}</span>;
}

function PortalPill({ s }) {
  const map = { published: ["bg-[#86EFAC]", "Live on Brand"], approved: ["bg-[#93C5FD]", "Approved"],
    pending_review: ["bg-[#FDE047]", "Pending Review"], rejected: ["bg-[#FCA5A5]", "Rejected"],
    not_requested: ["bg-slate-200", "—"] };
  const [c, t] = map[s] || ["bg-slate-200", s];
  return <span className={`nb-badge text-[10px] ${c}`}>{t}</span>;
}

// ── Shared editor, scoped dynamically by entityType/entityId ──────────────
export function PostEditor({ scope = "tenant", entityType, entityId, post, controls, onSaved, onClose }) {
  const blank = { title: "", excerpt: "", content_html: "", category: entityType === "store" ? "store" : "school",
    status: "draft", scheduled_at: "", tags: [], target_audiences: ["public"], linked_media_urls: [],
    post_to_own_socials: false, request_portal_socials: false };
  const [f, setF] = useState(post ? { ...blank, ...post, tags: post.tags || [],
    linked_media_urls: (post.media?.linked_media_urls) || [],
    post_to_own_socials: post.social_syndication?.post_to_own_socials || false,
    request_portal_socials: post.social_syndication?.request_portal_socials || false } : blank);
  const [saved, setSaved] = useState(post || null);
  const [aiTopic, setAiTopic] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const media = saved?.media?.direct_upload_files || [];
  const byokSet = controls?.custom_gemini_api_key__set;
  const storageUsed = controls?.storage_used_bytes || 0;
  const storageAllowed = controls?.storage_allowed_bytes || 0;
  const remaining = storageAllowed > 0 ? Math.max(storageAllowed - storageUsed, 0) : Infinity;
  const uploadsOn = controls?.allow_direct_media_upload;

  const save = async (statusOverride) => {
    if (!f.title.trim()) return toast.error("Title required");
    setBusy(true);
    const payload = { ...f, status: statusOverride || f.status, entity_type: entityType, entity_id: entityId };
    try {
      let res;
      if (saved?.id) res = await api.put(`/posts/${saved.id}`, payload);
      else res = await api.post("/posts", payload);
      setSaved(res.data);
      toast.success(saved?.id ? "Post updated" : "Draft saved");
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const runAI = async () => {
    if (scope !== "global" && !byokSet) return toast.error("Add your Gemini API key in Entity Controls to use AI.");
    if (!aiTopic.trim()) return toast.error("Enter a topic for the AI");
    setAiBusy(true);
    try {
      const { data } = await api.post("/posts/generate-ai-content", {
        prompt_topic: aiTopic, tone: "engaging", category: f.category,
        target_audience: (f.target_audiences || ["public"])[0] });
      setF((p) => ({ ...p, title: data.title || p.title, excerpt: data.excerpt || p.excerpt,
        content_html: data.content_html || p.content_html, tags: data.tags || p.tags }));
      toast.success("AI draft inserted — review & edit");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    }
    setAiBusy(false);
  };

  const uploadFile = async (e, mediaType) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!saved?.id) return toast.error("Save the draft first, then attach media.");
    if (remaining !== Infinity && file.size > remaining)
      return toast.error(`File too large — only ${fmtBytes(remaining)} remaining.`);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("media_type", mediaType);
    try {
      const { data } = await api.post(`/posts/${saved.id}/media`, fd);
      setSaved((p) => ({ ...p, media: { ...p.media, direct_upload_files: [...(p.media?.direct_upload_files || []), data.file] } }));
      toast.success("Media uploaded");
      onSaved && onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Upload failed");
    }
  };

  const removeMedia = async (fid) => {
    try {
      await api.delete(`/posts/${saved.id}/media/${fid}`);
      setSaved((p) => ({ ...p, media: { ...p.media, direct_upload_files: p.media.direct_upload_files.filter((m) => m.file_id !== fid) } }));
      toast.success("Media removed — storage reclaimed");
      onSaved && onSaved();
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="post-editor">
      <div className="lg:col-span-2 nb-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="display text-xl font-bold">{saved?.id ? "Edit Post" : "New Post"}</h3>
          {onClose && <button className="nb-btn-sm" onClick={onClose} data-testid="post-editor-close"><X size={14} /></button>}
        </div>
        <input className="nb-input font-bold text-lg" placeholder="Post title" value={f.title}
          onChange={(e) => set("title", e.target.value)} data-testid="post-title" />
        <textarea className="nb-input" rows={2} placeholder="Short excerpt" value={f.excerpt}
          onChange={(e) => set("excerpt", e.target.value)} data-testid="post-excerpt" />
        <textarea className="nb-input font-mono text-sm" rows={8} placeholder="Body (HTML supported)"
          value={f.content_html} onChange={(e) => set("content_html", e.target.value)} data-testid="post-body" />
        <div className="flex gap-2 flex-wrap">
          <input className="nb-input flex-1" placeholder="tags (comma separated)"
            value={(f.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
            data-testid="post-tags" />
        </div>
        {/* Media */}
        <div className="border-2 border-black rounded-lg p-3 bg-[#FAFAFA]">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-sm">Media</span>
            <span className="text-[11px] opacity-70">
              {uploadsOn ? <>Max 2 images or 1 video (≤10s). {remaining === Infinity ? "Unlimited" : fmtBytes(remaining) + " left"}</>
                         : "Direct uploads disabled — use linked URLs"}
            </span>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <label className={`nb-btn-sm cursor-pointer ${!uploadsOn && "opacity-40 pointer-events-none"}`} data-testid="post-upload-image">
              <ImageIcon size={14} className="inline mr-1" /> Add Image
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e, "image")} />
            </label>
            <label className={`nb-btn-sm cursor-pointer ${!uploadsOn && "opacity-40 pointer-events-none"}`} data-testid="post-upload-video">
              <Film size={14} className="inline mr-1" /> Add Video
              <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadFile(e, "video")} />
            </label>
          </div>
          <div className="flex gap-2 flex-wrap mt-2">
            {media.map((m) => (
              <div key={m.file_id} className="relative border border-black rounded p-1 text-[10px] bg-white" data-testid={`post-media-${m.file_id}`}>
                {m.media_type === "video" ? <Film size={24} /> : <ImageIcon size={24} />}
                <div>{fmtBytes(m.size_bytes)}</div>
                <button className="absolute -top-2 -right-2 bg-[#FCA5A5] rounded-full p-0.5 border border-black"
                  onClick={() => removeMedia(m.file_id)} data-testid={`post-media-del-${m.file_id}`}><Trash2 size={10} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Side panel */}
      <div className="space-y-4">
        <div className="nb-card p-4 space-y-2">
          <div className="flex items-center gap-2 font-bold text-sm"><Sparkles size={16} /> AI Assist</div>
          <div className="text-[11px] opacity-70">{scope === "global" ? "Platform Gemini key" : (byokSet ? "Your Gemini key ✓" : "BYOK key not set")}</div>
          <textarea className="nb-input text-sm" rows={2} placeholder="e.g. Diwali holiday notice for parents"
            value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} data-testid="ai-topic" />
          <button className="nb-btn w-full text-sm" onClick={runAI} disabled={aiBusy} data-testid="ai-generate-btn">
            {aiBusy ? "Generating…" : "Generate with Gemini"}
          </button>
        </div>
        <div className="nb-card p-4 space-y-2">
          <div className="font-bold text-sm">Publish</div>
          <label className="nb-label text-[11px]">Category</label>
          <select className="nb-select text-sm" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="post-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="nb-label text-[11px]">Audience</label>
          <div className="flex flex-wrap gap-1">
            {AUDIENCES.map((a) => (
              <button key={a} onClick={() => set("target_audiences", f.target_audiences.includes(a) ? f.target_audiences.filter((x) => x !== a) : [...f.target_audiences, a])}
                className={`nb-badge text-[10px] ${f.target_audiences.includes(a) ? "bg-[#86EFAC]" : "bg-white"}`}>{a}</button>
            ))}
          </div>
          <label className="nb-label text-[11px]">Status</label>
          <select className="nb-select text-sm" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="post-status">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {f.status === "scheduled" && (
            <input type="datetime-local" className="nb-input text-sm" value={f.scheduled_at || ""}
              onChange={(e) => set("scheduled_at", e.target.value)} data-testid="post-scheduled-at" />
          )}
        </div>
        <div className="nb-card p-4 space-y-2">
          <div className="font-bold text-sm">Social Auto-Share</div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={f.post_to_own_socials} onChange={(e) => set("post_to_own_socials", e.target.checked)}
              data-testid="post-own-socials" /> Post to our Facebook & LinkedIn
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={f.request_portal_socials} onChange={(e) => set("request_portal_socials", e.target.checked)}
              data-testid="post-portal-socials" /> Request feature on Ponderr Brand Pages
          </label>
          {saved?.social_syndication?.portal_social_status && <PortalPill s={saved.social_syndication.portal_social_status} />}
        </div>
        <div className="flex gap-2">
          <button className="nb-btn flex-1" onClick={() => save("draft")} disabled={busy} data-testid="post-save-draft">Save Draft</button>
          <button className="nb-btn-primary flex-1" onClick={() => save(f.status === "draft" ? "published" : f.status)} disabled={busy} data-testid="post-publish">
            <Send size={14} className="inline mr-1" />{f.status === "scheduled" ? "Schedule" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tenant console (School / Store / Academy / TV) ────────────────────────
export function TenantPosts({ entityType = "school" }) {
  const [posts, setPosts] = useState([]);
  const [controls, setControls] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/posts").then((r) => setPosts(r.data || [])).catch(() => {});
  const loadCtl = () => api.get("/posts/entity-controls").then((r) => setControls(r.data?.[0] || null)).catch(() => {});
  useEffect(() => { load(); loadCtl(); }, []);
  const entityId = controls?.entity_id;
  const storagePct = controls?.storage_allowed_bytes > 0 ? Math.min(100, (controls.storage_used_bytes / controls.storage_allowed_bytes) * 100) : 0;

  if (editing !== null) {
    return <PostEditor scope="tenant" entityType={controls?.entity_type || entityType} entityId={entityId}
      post={editing.id ? editing : null} controls={controls}
      onSaved={() => { load(); loadCtl(); }} onClose={() => { setEditing(null); load(); }} />;
  }
  return (
    <div className="space-y-4" data-testid="tenant-posts">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="display text-2xl font-bold">Posts & News</h2>
        <button className="nb-btn-primary" onClick={() => setEditing({})} data-testid="tenant-new-post">+ New Post</button>
      </div>
      {controls && (
        <div className="nb-card p-3 flex items-center gap-4 flex-wrap">
          <span className="text-sm font-bold">{controls.plan_tier} plan</span>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[11px] mb-1">Storage: {fmtBytes(controls.storage_used_bytes || 0)} / {controls.storage_allowed_bytes > 0 ? fmtBytes(controls.storage_allowed_bytes) : "∞"}</div>
            <div className="h-2 bg-slate-200 rounded"><div className="h-2 bg-[#86EFAC] rounded" style={{ width: `${storagePct}%` }} /></div>
          </div>
          <span className="nb-badge text-[10px] bg-white">{controls.cadence_limit?.replace(/_/g, " ")}</span>
        </div>
      )}
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Title</th><th>Category</th><th>Status</th><th>Brand</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} data-testid={`tenant-post-${p.id}`}>
                <td className="font-bold">{p.title}</td>
                <td className="text-xs">{p.category}</td>
                <td><StatusPill s={p.status} /></td>
                <td><PortalPill s={p.social_syndication?.portal_social_status} /></td>
                <td className="text-xs mono">{new Date(p.created_at).toLocaleDateString()}</td>
                <td><button className="nb-btn-sm" onClick={() => setEditing(p)} data-testid={`tenant-edit-${p.id}`}>Edit</button></td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No posts yet — create your first</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Global Admin: Posts & Media Hub (3 tabs) ──────────────────────────────
export function GlobalPostsHub() {
  const [tab, setTab] = useState("feed");
  const TABS = [["feed", "Master Feed & Drafts"], ["ai", "AI Creative Studio"], ["matrix", "Entity Controls & Storage"], ["subs", "Subscribers & Newsletters"]];
  return (
    <div className="space-y-4" data-testid="global-posts-hub">
      <h2 className="display text-2xl font-bold">Posts & Media Hub</h2>
      <div className="flex gap-2 flex-wrap">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={`nb-btn-sm ${tab === id ? "bg-[#C4B5FD]" : "bg-white"}`} data-testid={`gph-tab-${id}`}>{label}</button>
        ))}
      </div>
      {tab === "feed" && <MasterFeed />}
      {tab === "ai" && <AICreativeStudio />}
      {tab === "matrix" && <EntityMatrix />}
      {tab === "subs" && <SubscribersNewsletterTab />}
    </div>
  );
}

function MasterFeed() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [controls, setControls] = useState([]);
  const load = () => api.get("/posts").then((r) => setPosts(r.data || [])).catch(() => {});
  const loadCtl = () => api.get("/posts/entity-controls").then((r) => setControls(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadCtl(); }, []);
  const approve = async (id, decision) => {
    try { const { data } = await api.post(`/posts/${id}/portal-approval`, { decision });
      toast.success(`Cross-post ${data.portal_social_status}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const ctlOf = (eid) => controls.find((c) => c.entity_id === eid);
  if (editing !== null) {
    const ctl = ctlOf(editing.entity_id);
    return <PostEditor scope="global" entityType={editing.entity_type || "school"} entityId={editing.entity_id}
      post={editing.id ? editing : null} controls={ctl} onSaved={load} onClose={() => { setEditing(null); load(); }} />;
  }
  return (
    <div className="space-y-3" data-testid="master-feed">
      <button className="nb-btn-primary" onClick={() => setEditing({ entity_type: "platform", entity_id: "__platform__" })} data-testid="mf-new-platform-post">+ New Platform Post</button>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table text-sm">
          <thead><tr><th>Title</th><th>Category</th><th>Entity</th><th>Status</th><th>Brand Social</th><th>Date</th><th>Actions</th></tr></thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} data-testid={`mf-row-${p.id}`}>
                <td className="font-bold">{p.title}</td>
                <td className="text-xs">{p.category}</td>
                <td className="text-xs">{p.entity_id}</td>
                <td><StatusPill s={p.status} /></td>
                <td><PortalPill s={p.social_syndication?.portal_social_status} /></td>
                <td className="text-xs mono">{new Date(p.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <button className="nb-btn-sm" onClick={() => setEditing(p)} data-testid={`mf-edit-${p.id}`}>Edit</button>
                    {p.social_syndication?.portal_social_status === "pending_review" && (
                      <>
                        <button className="nb-badge text-[10px] bg-[#86EFAC]" onClick={() => approve(p.id, "approved")} data-testid={`mf-approve-${p.id}`}>Approve</button>
                        <button className="nb-badge text-[10px] bg-[#FCA5A5]" onClick={() => approve(p.id, "rejected")} data-testid={`mf-reject-${p.id}`}>Reject</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No posts across the platform yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AICreativeStudio() {
  const [inp, setInp] = useState({ prompt_topic: "", tone: "exciting", category: "quiz_olympiad", target_audience: "students" });
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!inp.prompt_topic.trim()) return toast.error("Enter a topic");
    setBusy(true);
    try { const { data } = await api.post("/posts/generate-ai-content", inp); setOut(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const pushDraft = async () => {
    try {
      await api.post("/posts", { title: out.title, excerpt: out.excerpt, content_html: out.content_html,
        category: inp.category, tags: out.tags, entity_type: "platform", entity_id: "__platform__", status: "draft" });
      toast.success("Pushed to platform drafts");
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="ai-creative-studio">
      <div className="nb-card p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold"><Sparkles size={18} /> AI Creative Studio</div>
        <input className="nb-input" placeholder="Topic — e.g. National Quiz Olympiad promo" value={inp.prompt_topic}
          onChange={(e) => setInp({ ...inp, prompt_topic: e.target.value })} data-testid="acs-topic" />
        <div className="grid grid-cols-2 gap-2">
          <select className="nb-select text-sm" value={inp.tone} onChange={(e) => setInp({ ...inp, tone: e.target.value })} data-testid="acs-tone">
            {["exciting", "professional", "friendly", "urgent", "playful"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <select className="nb-select text-sm" value={inp.category} onChange={(e) => setInp({ ...inp, category: e.target.value })} data-testid="acs-category">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <button className="nb-btn-primary w-full" onClick={gen} disabled={busy} data-testid="acs-generate">{busy ? "Generating…" : "Generate"}</button>
      </div>
      <div className="nb-card p-5 space-y-2">
        {!out ? <div className="text-slate-500 text-sm">Generated copy appears here.</div> : (
          <>
            <h3 className="font-bold text-lg" data-testid="acs-out-title">{out.title}</h3>
            <p className="text-sm opacity-80">{out.excerpt}</p>
            <div className="text-xs border-l-2 border-black pl-2" dangerouslySetInnerHTML={{ __html: out.content_html }} />
            <div className="flex flex-wrap gap-1">{(out.tags || []).map((t) => <span key={t} className="nb-badge text-[10px] bg-white">#{t}</span>)}</div>
            <div className="text-xs space-y-1">
              {out.social_captions && Object.entries(out.social_captions).map(([k, v]) => (
                <div key={k}><b className="capitalize">{k}:</b> {v}</div>
              ))}
            </div>
            <button className="nb-btn w-full mt-2" onClick={pushDraft} data-testid="acs-push-draft">Push to Platform Draft</button>
          </>
        )}
      </div>
    </div>
  );
}

function EntityMatrix() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/posts/entity-controls").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const patch = async (eid, body) => {
    try { await api.put(`/posts/entity-controls/${eid}`, body); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid="entity-matrix">
      <div className="overflow-x-auto">
        <table className="nb-table text-sm" style={{ minWidth: 1050 }}>
          <thead><tr><th>Entity</th><th>Type</th><th>Plan</th><th>Landing</th><th>Brand Cross-Post</th><th>Storage</th><th>Uploads</th><th>Cadence</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const pct = r.storage_allowed_bytes > 0 ? Math.min(100, (r.storage_used_bytes / r.storage_allowed_bytes) * 100) : 0;
              return (
                <tr key={r.entity_id} data-testid={`em-row-${r.entity_id}`}>
                  <td className="font-bold">{r.entity_name}</td>
                  <td className="text-xs">{r.entity_type}</td>
                  <td><span className="nb-badge text-[10px] bg-white">{r.plan_tier}</span></td>
                  <td><button className={`nb-badge text-[10px] ${r.can_post_landing ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`}
                    onClick={() => patch(r.entity_id, { can_post_landing: !r.can_post_landing })} data-testid={`em-landing-${r.entity_id}`}>{r.can_post_landing ? "On" : "Off"}</button></td>
                  <td><button className={`nb-badge text-[10px] ${r.can_post_portal_socials ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`}
                    onClick={() => patch(r.entity_id, { can_post_portal_socials: !r.can_post_portal_socials })} data-testid={`em-portal-${r.entity_id}`}>{r.can_post_portal_socials ? "On" : "Off"}</button></td>
                  <td className="text-xs min-w-[140px]">
                    {fmtBytes(r.storage_used_bytes || 0)} / {r.storage_allowed_bytes > 0 ? fmtBytes(r.storage_allowed_bytes) : "∞"}
                    <div className="h-1.5 bg-slate-200 rounded mt-1"><div className="h-1.5 bg-[#93C5FD] rounded" style={{ width: `${pct}%` }} /></div>
                  </td>
                  <td><button className={`nb-badge text-[10px] ${r.allow_direct_media_upload ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`}
                    onClick={() => patch(r.entity_id, { allow_direct_media_upload: !r.allow_direct_media_upload })} data-testid={`em-uploads-${r.entity_id}`}>{r.allow_direct_media_upload ? "On" : "Off"}</button></td>
                  <td className="text-xs">{(r.cadence_limit || "").replace(/_/g, " ")}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">No entities</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const AUDIENCE_OPTS = [["", "All Audiences"], ["student", "Students Only"], ["school_leader", "School Leaders"],
  ["bookstore_vendor", "Bookstores"], ["academy_creator", "Academies"], ["parent", "Parents"], ["educator", "Teachers"]];

function Metric({ label, value }) {
  return (
    <div className="nb-card p-4 text-center">
      <div className="display text-3xl font-black">{value}</div>
      <div className="text-xs opacity-60">{label}</div>
    </div>
  );
}

function SubscribersNewsletterTab() {
  const [metrics, setMetrics] = useState({});
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [roleF, setRoleF] = useState("all");
  const [nl, setNl] = useState({ title: "", summary: "", mode: "pdf_upload", pdf_file_url: "", audience: "" });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadMetrics = () => api.get("/subscribers/metrics").then((r) => setMetrics(r.data)).catch(() => {});
  const loadSubs = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (roleF && roleF !== "all") p.set("role", roleF);
    api.get(`/subscribers?${p.toString()}`).then((r) => setSubs(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadMetrics(); }, []);
  useEffect(() => { loadSubs(); }, [q, roleF]);

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/newsletters/upload-pdf", fd); setNl((p) => ({ ...p, pdf_file_url: data.pdf_url })); toast.success("PDF uploaded"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const dispatch = async (pushNow) => {
    if (!nl.title.trim()) return toast.error("Campaign title required");
    setBusy(true);
    try {
      const { data } = await api.post("/newsletters/dispatch", {
        title: nl.title, summary: nl.summary,
        media_type: nl.mode, pdf_file_url: nl.pdf_file_url || null,
        target_roles: nl.audience ? [nl.audience] : [], push_now: pushNow,
        scheduled_at: pushNow ? null : new Date(Date.now() + 3600000).toISOString(),
      });
      toast.success(pushNow ? `Dispatched to ${data.stats.total_targets} subscribers (${data.stats.sent_count} sent)` : "Scheduled");
      loadMetrics();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dispatch failed"); }
    setBusy(false);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-4" data-testid="subscribers-newsletter-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Total Subscribers" value={metrics.total ?? 0} />
        <Metric label="School Leaders" value={metrics.school_leaders ?? 0} />
        <Metric label="Students & Parents" value={metrics.students_parents ?? 0} />
        <Metric label="Vendors & Academies" value={metrics.vendors_academies ?? 0} />
      </div>

      {/* Section A: Dispatcher */}
      <div className="nb-card p-5 space-y-3">
        <h3 className="display text-xl font-bold">Newsletter & PDF Dispatcher</h3>
        <input className="nb-input" placeholder="Campaign title" value={nl.title} onChange={(e) => setNl({ ...nl, title: e.target.value })} data-testid="nl-title" />
        <textarea className="nb-input" rows={2} placeholder="Brief notes / summary" value={nl.summary} onChange={(e) => setNl({ ...nl, summary: e.target.value })} data-testid="nl-summary" />
        <div className="flex gap-2">
          <button className={`nb-btn-sm ${nl.mode === "pdf_upload" ? "bg-[#C4B5FD]" : "bg-white"}`} onClick={() => setNl({ ...nl, mode: "pdf_upload" })} data-testid="nl-mode-pdf">📄 Upload PDF Newsletter</button>
          <button className={`nb-btn-sm ${nl.mode === "curated_posts_digest" ? "bg-[#C4B5FD]" : "bg-white"}`} onClick={() => setNl({ ...nl, mode: "curated_posts_digest" })} data-testid="nl-mode-digest">📰 Auto-compile Recent Posts Digest</button>
        </div>
        {nl.mode === "pdf_upload" && (
          <div className="flex items-center gap-3">
            <label className="nb-btn-sm cursor-pointer" data-testid="nl-pdf-upload">{uploading ? "Uploading…" : "Choose PDF"}<input type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} /></label>
            {nl.pdf_file_url && <a href={nl.pdf_file_url} target="_blank" rel="noreferrer" className="text-sm underline" data-testid="nl-pdf-link">Preview PDF</a>}
          </div>
        )}
        <div className="flex flex-wrap gap-3 items-center">
          <select className="nb-select w-auto text-sm" value={nl.audience} onChange={(e) => setNl({ ...nl, audience: e.target.value })} data-testid="nl-audience">
            {AUDIENCE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="nb-btn-primary" onClick={() => dispatch(true)} disabled={busy} data-testid="nl-push">🚀 Push to Subscribers Now</button>
          <button className="nb-btn" onClick={() => dispatch(false)} disabled={busy} data-testid="nl-schedule">🕒 Schedule Dispatch</button>
        </div>
      </div>

      {/* Section B: Directory */}
      <div className="nb-card p-0 overflow-hidden">
        <div className="p-3 flex items-center gap-2 flex-wrap border-b-2 border-black bg-white">
          <input className="nb-input flex-1 min-w-[180px]" placeholder="Search name / phone / city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="subs-search" />
          <select className="nb-select w-auto text-sm" value={roleF} onChange={(e) => setRoleF(e.target.value)} data-testid="subs-role-filter">
            <option value="all">All roles</option>
            {["student", "educator", "school_leader", "bookstore_vendor", "academy_creator", "parent", "other"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button className="nb-btn-sm" onClick={exportCsv} data-testid="subs-export">📥 Export CSV</button>
        </div>
        <div className="max-h-[50vh] overflow-auto">
          <table className="nb-table text-sm">
            <thead><tr><th>Date</th><th>Name</th><th>WhatsApp</th><th>Email</th><th>Role</th><th>Details</th><th>Status</th></tr></thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} data-testid={`subs-row-${s.id}`}>
                  <td className="text-xs mono">{(s.subscribed_at || "").slice(0, 10)}</td>
                  <td className="font-bold">{s.full_name}</td>
                  <td className="mono text-xs">{s.phone}</td>
                  <td className="text-xs">{s.email || "—"}</td>
                  <td><span className="nb-badge text-[10px] bg-white">{s.role}</span></td>
                  <td className="text-xs">{Object.entries(s.role_metadata || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ") || "—"}</td>
                  <td><span className={`nb-badge text-[10px] ${s.status === "active" ? "bg-[#86EFAC]" : "bg-slate-200"}`}>{s.status}</span></td>
                </tr>
              ))}
              {subs.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No subscribers yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

